<template>
  <AuthLayout>
    <form
      v-if="!emailSent"
      @submit.prevent
    >
      <h3 class="mb-5 font-medium text-primary">Enter your email to reset your password</h3>

      <div class="flex flex-col">
        <InputGroup>
          <InputGroupAddon>
            <Icon type="envelope" />
          </InputGroupAddon>
          <Input
            v-model="formObject.email"
            required
            type="email"
            class="w-full"
            placeholder="Email"
            autocomplete="email"
            :has-error="v$.email.$error"
          />
        </InputGroup>
        <InputError :has-error="v$.email.$error">{{ v$.email.$errors[0]?.$message }}</InputError>
      </div>

      <Button
        label="Send password reset email"
        type="submit"
        block
        :is-loading="isSubmitting"
        class="mt-5"
        @click="submit"
      />
      <Button
        text
        label="Back to Login"
        router-link="/login"
        block
        class="mt-5"
      />
    </form>
    <div
      v-else
      class="text-center"
    >
      <Icon
        type="circle-check"
        class="text-success mb-4 text-4xl"
      />
      <div class="text-3xl">Email Sent</div>
      <div class="text-secondary mt-2">Check your inbox for reset instructions</div>

      <Button
        label="Back to Login"
        type="submit"
        outlined
        block
        class="mt-8"
        router-link="/login"
      />
    </div>
  </AuthLayout>
</template>

<script setup lang="ts">
// components
import Icon from '@/components/icon/Icon.vue'
import Button from '@/components/button/Button.vue'
import InputGroup from '@/components/forms/InputGroup.vue'
import InputGroupAddon from '@/components/forms/InputGroupAddon.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'

// views
import AuthLayout from '@/views/auth/AuthLayout.vue'

// composables
import { ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required, email, helpers } from '@vuelidate/validators'
import useApiRequest from '@/composables/useApiRequest'

/** Flag indicating submit in progress */
const isSubmitting = ref<boolean>(false)
/** Flag indicating reset email has been sent */
const emailSent = ref<boolean>(false)

/** Form data object */
const formObject = ref<{ email: string | undefined }>({
  email: import.meta.env.VITE_DEV_USER_EMAIL || undefined
})

/** Vuelidate rules */
const formRules = {
  email: {
    required: helpers.withMessage('Enter your Email', required),
    email: helpers.withMessage('Enter a valid email address', email)
  }
}

/** Vuelidate instance */
const v$ = useVuelidate(formRules, formObject)

/**
 * Submit forgot password request.
 */
async function submit(): Promise<void> {
  try {
    const isFormCorrect = await v$.value.$validate()
    if (isFormCorrect) {
      isSubmitting.value = true

      await useApiRequest().send({
        method: 'POST',
        endpoint: '/v1/auth/forgot-password',
        data: { email: formObject.value.email }
      })
      emailSent.value = true
      isSubmitting.value = false
    }
  } catch (err) {
    isSubmitting.value = false
    console.error('Forgot password submission failed', err)
  }
}
</script>

<style scoped></style>
